import { useToggleObject, useAlert } from '@uplift/ui-v2';
import { DischargeNoteStepProps } from './DischargeNotePage';
import { DischargeNoteLayout } from './DischargeNoteLayout';
import { DischargeNoteActionBar } from './DischargeNoteActionBar';
import { useHistory } from 'react-router';
import { providerPatientRoute } from '@uplift/constants';
import { SubmitAndCancelUpcomingSessionsModal } from './SubmitAndCancelUpcomingSessionsModal';
import { useMutation, useQuery } from '@tanstack/react-query';
import { nextSessions, updateDischargeNote } from '@uplift/api';
import { useCurrentuser } from '@uplift/contexts';
import { DischargeNoteReview } from './DischargeNoteReview';
import { isAxiosError } from 'axios';

export const ReviewStep: React.VFC<DischargeNoteStepProps> = props => {
  const me = useCurrentuser();
  const history = useHistory();
  const snackbar = useAlert();
  const { open, close, value: isOpen } = useToggleObject();
  const saveNoteMutation = useMutation({
    mutationKey: [ 'save-discharge-note' ],
    mutationFn: () => updateDischargeNote(props.noteId, { isSubmitted: true }),
    onSuccess: () => {
      history.replace(providerPatientRoute.createLink({ patientId: props.patientId }));
      snackbar.success('Discharge note submitted');
    },
    onError: (error) => {
      let message = 'Unable to submit note';

      if (isAxiosError(error)) {
        const responseErrorMessage = error?.response?.data?.error;

        if (responseErrorMessage) {
          message += `: ${responseErrorMessage}`;
        }
      }

      snackbar.error(message);
    }
  });
  const { data: upcomingSessions = [] } = useQuery({
    queryKey: [ 'next-patient-sessions' ],
    queryFn: async () => {
      const response = await nextSessions({ patient_id: props.patientId, provider_id: me.id, limit: 1 });

      return response.data;
    }
  });


  const onSubmit = () => {
    saveNoteMutation.mutate();
  };

  const onNext = () => {
    if (upcomingSessions.length) {
      open();
      return;
    }

    onSubmit();
  };

  const onCloseModal = async (proceed: boolean) => {
    if (proceed) {
      onSubmit();
    }

    close();
  };

  const onSaveAndFinishLater = () => {
    history.push(providerPatientRoute.createLink({ patientId: props.patientId }));
  };

  return (
    <DischargeNoteLayout
      background="grey"
      onBack={() => props.setStep('form')}
      actionBar={(
        <DischargeNoteActionBar
          onSaveAndFinishLater={onSaveAndFinishLater}
          nextText="Sign and Submit"
          onNext={onNext}
          noteId={props.noteId}
          patientId={props.patientId}
          isLoading={saveNoteMutation.isLoading}
        />
      )}
    >
      <SubmitAndCancelUpcomingSessionsModal open={isOpen} onClose={onCloseModal} />

      <DischargeNoteReview patientId={props.patientId} noteId={props.noteId} />
    </DischargeNoteLayout>
  );
};
